import { ListBox as AriaListBox, type Key } from "react-aria-components";

import { DropdownListBoxSearchBar } from "@/components/ui/inputs/Dropdown/DropdownListBoxSearchBar";

import type { DropdownProps } from "./Dropdown";
import { DropdownListBoxAllOption } from "./DropdownListBoxAllOption";
import { DropdownListBoxSelectionBar } from "./DropdownListBoxSelectionBar";
import { DropdownListItem } from "./DropdownListItem";

type DropdownListBoxProps<TKey extends Key = Key> = DropdownProps<TKey> & {
  header?: React.ReactNode;
  onSearchChange?: (value: string) => void;
  search?: string;
};

export const DropdownListBox = <TKey extends Key = Key>({
  selectionMode,
  value,
  onChange,
  items,
  showSelectionBar,
  showAllOption,
  showSearch,
  header,
  onSearchChange,
  search,
}: DropdownListBoxProps<TKey>) => {
  const isMultiple = selectionMode === "multiple";

  const getSelectedKeys = () => {
    if (isMultiple) {
      return value;
    }

    return typeof value === "undefined" ? [] : [value];
  };

  return (
    <>
      <div className="max-h-[300px] flex-1 overflow-y-auto outline-none">
        {header}

        {isMultiple && (
          <>
            {showSelectionBar && (
              <DropdownListBoxSelectionBar
                onChange={onChange}
                items={items}
                className="t:flex hidden"
              />
            )}

            {showSearch && (
              <DropdownListBoxSearchBar
                onSearchChange={onSearchChange}
                search={search}
              />
            )}

            {showAllOption && (
              <DropdownListBoxAllOption
                value={value}
                onChange={onChange}
                items={items}
              />
            )}
          </>
        )}

        <AriaListBox
          selectionMode={selectionMode}
          items={items}
          selectedKeys={getSelectedKeys()}
          onSelectionChange={(id) => {
            if (id === "all" && isMultiple) {
              onChange(items.map((item) => item.id));
              return;
            }

            const selectedIds = Array.from(id);
            if (!isMultiple && selectedIds.length === 0) {
              if (value) {
                onChange(value);
              }
              return;
            }

            if (isMultiple) {
              onChange(selectedIds as TKey[]);
            } else {
              onChange(selectedIds[0] as TKey);
            }
          }}
        >
          {items.map((item) => (
            <DropdownListItem
              {...item}
              selectionMode={selectionMode}
            />
          ))}
        </AriaListBox>
      </div>

      {isMultiple && (
        <DropdownListBoxSelectionBar
          onChange={onChange}
          items={items}
          className="t:hidden bg-elevation-surface-1"
        />
      )}
    </>
  );
};
